import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { isSuperAdmin } from '@/utils/auth';
import { ENABLED_GAME_TYPES } from '@/lib/platform-filters';
import { useState } from 'react';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { useGetPickemsQuery } from '@/api/pickems/getPickems';
import { useGetSquaresQuery } from '@/api/squares/getSquares';
import { useGetDailyRevealsQuery } from '@/api/daily-reveals/getDailyReveals';
import { useCreateTenantMutation } from '@/api/tenants/createTenant';
import { SettingsPageTemplate } from '@/components/templates/SettingsPageTemplate';
import { useSettingsPage } from '@/hooks/useSettingsPage';
import { Building2, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { createTableActions } from '@/utils/actionFactory';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const Route = createFileRoute('/dashboard/tenants/')({
  beforeLoad: () => {
    if (!isSuperAdmin()) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: TenantsPage,
});

function TenantsPage() {
  const navigate = useNavigate();
  const { data: tenants = [], isLoading, refetch } = useGetTenantsQuery();
  const { data: pickems = [] } = useGetPickemsQuery();
  const { data: squares = [] } = useGetSquaresQuery();
  const { data: dailyReveals = [] } = useGetDailyRevealsQuery();

  // Count games per tenant, broken down by game type
  const getPickemCount = (tenantId: string) =>
    pickems.filter((p: any) => p.tenant_id === tenantId).length;

  const getSquaresCount = (tenantId: string) =>
    squares.filter((s: any) => s.tenant_id === tenantId).length;

  const getDailyRevealCount = (tenantId: string) =>
    dailyReveals.filter((d: any) => d.tenant_id === tenantId).length;

  // Per-game count/column config, keyed by sports-config game type code.
  const countByGameType: Record<string, (tenantId: string) => number> = {
    pickem: getPickemCount,
    squares: getSquaresCount,
    daily_reveal: getDailyRevealCount,
  };
  // The pickem column id is 'pickems' (plural) for historical reasons; every
  // other column id matches its game type code.
  const columnIdByGameType: Record<string, string> = {
    pickem: 'pickems',
  };

  const formattedTenants = tenants.map(tenant => ({
    ...tenant,
    id: tenant.tenant_id,
    label: tenant.title || tenant.name_en || tenant.name_fr || '',
  }));

  const {
    searchQuery,
    setSearchQuery,
    filteredData: filteredTenants,
  } = useSettingsPage({
    data: formattedTenants,
    isLoading,
    refetch,
    settingsKey: 'tenants',
    editRoutePath: '/dashboard/tenant/$tenantCode',
    getSearchFields: (tenant: any) => [
      tenant.name_en || '',
      tenant.name_fr || '',
      tenant.description_en || '',
      tenant.description_fr || '',
    ],
  });

  const columns = [
    {
      id: 'code',
      name: 'Code',
      render: (tenant: any) => <Badge variant="outline">{tenant.code || '-'}</Badge>,
    },
    {
      id: 'name_en',
      name: 'Name',
      className: 'font-medium',
      render: (tenant: any) => tenant.title || tenant.name_en || tenant.name_fr || 'Untitled',
    },
    // Per-game count columns only for games this client deployed (Layer A).
    ...ENABLED_GAME_TYPES.map(gameType => ({
      id: columnIdByGameType[gameType.code] ?? gameType.code,
      name: gameType.label,
      render: (tenant: any) => countByGameType[gameType.code]?.(tenant.tenant_id) ?? 0,
    })),
  ];

  const actions = createTableActions({
    onEdit: (tenant: any) => {
      const code = tenant.code || tenant.slug;
      if (code) {
        navigate({
          to: '/dashboard/tenant/$tenantCode/branding',
          params: { tenantCode: code },
        });
      }
    },
  });

  // Relabel the actions
  actions.forEach((a: any) => {
    if (a.label === 'Edit') a.label = 'Settings';
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const createMutation = useCreateTenantMutation();

  const handleCreate = () => {
    if (!newCode.trim() || !newTitle.trim()) return;
    createMutation.mutate(
      {
        code: newCode.toLowerCase().trim(),
        title: newTitle.trim(),
        name_en: newTitle.trim(),
        description_en: newDescription.trim(),
      } as any,
      {
        onSuccess: () => {
          setCreateOpen(false);
          setNewCode('');
          setNewTitle('');
          setNewDescription('');
        },
      }
    );
  };

  const headerActions = (
    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="w-4 h-4 mr-1" />
          Create Tenant
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Tenant</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="tenant-code">Code (slug)</Label>
            <Input
              id="tenant-code"
              placeholder="e.g. nbc, espn"
              value={newCode}
              onChange={e => setNewCode(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
            />
            <p className="text-xs text-muted-foreground">
              Lowercase identifier used in URLs. Letters, numbers, hyphens, underscores only.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tenant-title">Title</Label>
            <Input
              id="tenant-title"
              placeholder="e.g. NBC Sports"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tenant-description">Description (optional)</Label>
            <Input
              id="tenant-description"
              placeholder="Brief description..."
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
            />
          </div>
          <Button
            onClick={handleCreate}
            disabled={!newCode.trim() || !newTitle.trim() || createMutation.isPending}
            className="w-full"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Tenant'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <SettingsPageTemplate
      title="Tenants"
      subtitle="Manage tenant settings and configurations"
      data={filteredTenants}
      isLoading={isLoading}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder="Search tenants by name or description..."
      columns={columns}
      actions={actions}
      headerActions={headerActions}
      onRowClick={(tenant: any) => {
        const code = tenant.code || tenant.slug;
        if (code) {
          navigate({
            to: '/dashboard/tenant/$tenantCode/branding',
            params: { tenantCode: code },
          });
        }
      }}
      emptyMessage="No tenants found"
      emptyState={{
        icon: Building2,
        title: 'No tenants configured',
        description: 'Tenants are managed at the system level.',
        actionLabel: undefined,
        onAction: undefined,
      }}
      breadcrumbs={<Breadcrumbs />}
    />
  );
}
